import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { LoadConfigurationFromStorage } from './configurator/configurator.actions';

type ParentMsg =
  | { type: 'SET_FROM_PARENT'; key: 'configJson'; value: string }
  | { type: 'SET_FROM_PARENT'; key: 'configId'; value: string };

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private parentOrigin: string | null = null;

  private readonly onMessage = (event: MessageEvent) => {
    // console.log('[CHILD] 📨 Raw message received:', {
    //   origin: event.origin,
    //   parentOrigin: this.parentOrigin,
    //   data: event.data
    // });

    // Security: only accept from real parent (derived from referrer)
    if (this.parentOrigin && event.origin !== this.parentOrigin) {
      console.warn('[CHILD] ❌ Origin mismatch, ignoring', {
        expected: this.parentOrigin,
        got: event.origin
      });
      return;
    }

    const data = event.data as any;
    if (!data || typeof data !== 'object') {
      console.warn('[CHILD] ❌ Invalid data format');
      return;
    }

    //console.log('[CHILD] Message type:', data.type);

    if (data.type !== 'SET_FROM_PARENT') {
      //console.log('[CHILD] ⏭️ Not SET_FROM_PARENT, ignoring');
      return;
    }

    const key = String(data.key || '');
    const value = data.value;

    //console.log('[CHILD] 🔑 Key:', key, 'Value length:', value?.length);

    if (key === 'configJson') {
      if (typeof value !== 'string' || !value) {
        console.error('[CHILD] ❌ configJson value invalid');
        return;
      }

      try {
        JSON.parse(value);
        //console.log('[CHILD] ✅ JSON validation passed');
      } catch (e) {
        console.error('[CHILD] ❌ JSON validation failed:', e);
        return;
      }

      //console.log('[CHILD] 💾 About to save to localStorage...');

      this.zone.run(() => {
        localStorage.setItem('wasombouw_configuration', value);
        //console.log('[CHILD] ✅ Saved to localStorage');

        this.store.dispatch(new LoadConfigurationFromStorage());
        //console.log('[CHILD] ✅ Dispatched LoadConfigurationFromStorage');

        const target = this.parentOrigin || '*';
        window.parent.postMessage({ type: 'CHILD_ACK', key: 'configJson' }, target);
        //console.log('[CHILD] ✅ Sent ACK to parent');
      });

      return;
    }

    if (key === 'configId') {
      //console.log('[CHILD] 💾 Saving configId...');
      if (typeof value !== 'string' || !value) return;
      this.zone.run(() => {
        localStorage.setItem('configId', value);
        //console.log('[CHILD] ✅ Saved configId');
      });
      return;
    }

    console.warn('[CHILD] ⚠️ Unknown key:', key);
  };

  constructor(
    private translate: TranslateService,
    private store: Store,
    private zone: NgZone
  ) {
    //console.log('[CHILD] 🏗️ AppComponent constructor called');
    translate.setDefaultLang('nl');
    translate.use('nl');
  }

  ngOnInit() {
    // console.log('[CHILD] 🚀 ngOnInit called');
    // console.log('[CHILD] 📍 Current URL:', window.location.href);
    // console.log('[CHILD] 👨‍👦 Referrer:', document.referrer);

    // Derive parent origin reliably
    try {
      this.parentOrigin = document.referrer ? new URL(document.referrer).origin : null;
      //console.log('[CHILD] ✅ Parent origin set to:', this.parentOrigin);
    } catch (e) {
      console.error('[CHILD] ❌ Failed to parse referrer:', e);
      this.parentOrigin = null;
    }

    //console.log('[CHILD] 👂 Adding message listener...');
    window.addEventListener('message', this.onMessage);

    // Tell parent we're ready
    const target = this.parentOrigin || '*';
    //.log('[CHILD] 📤 Sending IFRAME_READY to:', target);

    const sendReady = () => {
      window.parent.postMessage({ type: 'IFRAME_READY' }, target);
      //.log('[CHILD] 📤 IFRAME_READY sent');
    };

    sendReady();
    setTimeout(() => {
      //console.log('[CHILD] 📤 Sending IFRAME_READY (retry 1)');
      sendReady();
    }, 250);
    setTimeout(() => {
      //console.log('[CHILD] 📤 Sending IFRAME_READY (retry 2)');
      sendReady();
    }, 750);

    //console.log('[CHILD] 📦 Dispatching LoadConfigurationFromStorage (initial)...');
    this.store.dispatch(new LoadConfigurationFromStorage());
    //console.log('[CHILD] ✅ Initial dispatch complete');
  }

  ngOnDestroy() {
    //console.log('[CHILD] 💀 ngOnDestroy called');
    window.removeEventListener('message', this.onMessage);
  }
}